import { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { isRecord } from '../../../utils/typeGuards';
import { AddRowButton, DeleteRowButton, inlineFieldGroupClasses } from './sharedComponents';
import type { FieldOptions } from '../types';
import { useFormFieldComponentResolver } from '../fieldComponentResolverContext';

export interface FieldArrayItemFieldSettings {
    name: string;
    label: string;
    type: string;
    helpText?: string;
    readOnly?: boolean;
    [key: string]: unknown;
}

interface FieldArrayFieldProps {
    name: string;
    itemFields: readonly FieldArrayItemFieldSettings[];
    maxRows: number;
    disabled: boolean;
    createEmptyItem: (currentRows: readonly Record<string, unknown>[]) => Record<string, unknown>;
    onBeforeRemoveItem?: (removedRow: Record<string, unknown>, index: number) => void;
}

type FieldArrayFormValues = Record<string, unknown>;

function toRecordRows(value: unknown): Record<string, unknown>[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((entry): entry is Record<string, unknown> => isRecord(entry));
}

export function FieldArrayField({
    name,
    itemFields,
    maxRows,
    disabled,
    createEmptyItem,
    onBeforeRemoveItem,
}: FieldArrayFieldProps): JSX.Element {
    const { control, getValues } = useFormContext<FieldArrayFormValues>();
    const resolveFieldComponent = useFormFieldComponentResolver();
    const { fields, append, remove } = useFieldArray({
        control,
        name: name as never,
    });
    const watchedRows = useWatch<FieldArrayFormValues>({
        control,
        name,
    });
    const rowValues = toRecordRows(watchedRows);
    const rowLimitReached = fields.length >= maxRows;

    const manager = useMemo(
        () => ({
            addRow() {
                append(createEmptyItem(rowValues));
            },
            removeRow(index: number) {
                const nextRows = toRecordRows(getValues(name as never));
                const removedRow = nextRows[index];
                if (removedRow) {
                    onBeforeRemoveItem?.(removedRow, index);
                }
                remove(index);
            },
        }),
        [append, createEmptyItem, getValues, name, onBeforeRemoveItem, remove, rowValues]
    );

    return (
        <div className="flex flex-col gap-y-2.5">
            {fields.map((rowField, rowIndex) => (
                <div
                    key={rowField.id}
                    className={inlineFieldGroupClasses}
                    data-testid={`${name}-row-${rowIndex}`}
                >
                    {itemFields.map((itemField) => {
                        const fieldName = `${name}.${rowIndex}.${itemField.name}`;
                        const FieldComponent = resolveFieldComponent(
                            itemField.type,
                            itemField.name
                        );

                        return (
                            <FieldComponent
                                key={fieldName}
                                fieldOptions={
                                    {
                                        name: fieldName,
                                        settings: {
                                            ...itemField,
                                            readOnly: disabled || itemField.readOnly === true,
                                        },
                                    } as FieldOptions
                                }
                            />
                        );
                    })}
                    {fields.length > 0 ? (
                        <DeleteRowButton manager={manager} index={rowIndex} disabled={disabled} />
                    ) : null}
                </div>
            ))}
            {!rowLimitReached ? (
                <AddRowButton manager={manager} disabled={disabled} className="w-fit" />
            ) : null}
        </div>
    );
}
